import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, StyleSheet, Text, View } from 'react-native';
import {
  Cast,
  Check,
  ChevronLeft,
  ChevronRight,
  Minus,
  Pause,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Square,
  Unplug,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react-native';
import { imageSource } from '../api/jellyfin';
import { useRemotePlayback } from '../session/RemotePlaybackProvider';
import {
  formatRemoteTime,
  getRemoteProgress,
  getRemoteSessionCommands,
  getRemoteSessionId,
} from '../player/remotePlayback';
import { useSession } from '../session/SessionProvider';
import { colors, systemSansFont } from '../theme/tokens';
import { isTelevisionRuntime } from '../utils/platformCapabilities';
import { useReferenceWindowDimensions } from '../utils/referenceViewport';
import { FocusablePressable } from './FocusablePressable';
import { MediaImage } from './MediaImage';

type Props = {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  sideRail?: boolean;
};

function RemoteButton({ label, disabled = false, emphasized = false, onPress, children }: {
  label: string;
  disabled?: boolean;
  emphasized?: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) {
  return (
    <FocusablePressable
      accessibilityLabel={label}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={(focused, pressed, hovered) => [
        styles.transportButton,
        emphasized && styles.transportButtonPrimary,
        (focused || hovered) && styles.transportButtonActive,
        pressed && styles.pressed,
        disabled && styles.disabled,
      ]}
    >
      {children}
    </FocusablePressable>
  );
}

export function RemotePlaybackControl({ open, onToggle, onClose, sideRail = false }: Props) {
  const { credentials } = useSession();
  const { width } = useReferenceWindowDimensions();
  const remote = useRemotePlayback();
  const television = isTelevisionRuntime();
  const [showTargets, setShowTargets] = useState(!remote.selectedSession);
  const [triggerHovered, setTriggerHovered] = useState(false);
  const [timelineWidth, setTimelineWidth] = useState(1);
  const reveal = useRef(new Animated.Value(0)).current;
  const selected = remote.selectedSession;
  const nowPlaying = selected?.NowPlayingItem;
  const progress = selected ? getRemoteProgress(selected) : { positionTicks: 0, durationTicks: 0, ratio: 0 };
  const commands = selected ? getRemoteSessionCommands(selected) : [];
  const supports = (name: string) => commands.length === 0 || commands.includes(name) || commands.includes('Playstate');

  useEffect(() => {
    if (!open) {
      reveal.setValue(0);
      return;
    }
    setShowTargets(!selected);
    Animated.spring(reveal, {
      toValue: 1,
      speed: 24,
      bounciness: 2,
      useNativeDriver: true,
    }).start();
  }, [open, reveal, selected]);

  const issue = (operation: Promise<void>) => { void operation.catch(() => undefined); };
  const seekBy = (seconds: number) => issue(remote.seek(progress.positionTicks + seconds * 10_000_000));
  const volume = Math.max(0, Math.min(100, selected?.PlayState?.VolumeLevel ?? 100));

  return (
    <View style={styles.container}>
      <FocusablePressable
        focusId="shell.utility.cast"
        focusGroup="shell.utility"
        focusRole="control"
        focusDestinations={{ up: 'shell.utility.profile' }}
        accessibilityLabel={selected ? `Remote playback on ${selected.DeviceName ?? 'player'}` : 'Play on another device'}
        accessibilityState={{ selected: Boolean(selected), expanded: open }}
        onPress={onToggle}
        onHoverIn={() => setTriggerHovered(true)}
        onHoverOut={() => setTriggerHovered(false)}
        style={(focused, pressed, hovered) => [
          styles.trigger,
          selected && styles.triggerSelected,
          (focused || hovered) && styles.triggerActive,
          pressed && styles.pressed,
        ]}
      >
        <Cast color={selected ? '#fff' : colors.textMuted} size={television ? 24 : 20} strokeWidth={selected ? 2.4 : 1.9} />
        {selected ? <View style={styles.liveDot} /> : null}
        {sideRail && triggerHovered ? <View pointerEvents="none" style={styles.sideUtilityHoverLabel}><Text style={styles.sideUtilityLabelText}>Cast</Text></View> : null}
      </FocusablePressable>

      {open ? (
        <Animated.View
          accessibilityLabel="Remote playback panel"
          style={[
            styles.panel,
            sideRail && styles.sidePanel,
            television && styles.panelTelevision,
            { width: Math.min(television ? 430 : 360, Math.max(280, width - 24)) },
            {
              opacity: reveal,
              transform: [{ translateY: reveal.interpolate({ inputRange: [0, 1], outputRange: [-6, 0] }) }],
            },
          ]}
        >
          <View style={styles.header}>
            <View style={styles.headerCopy}>
              <Text numberOfLines={1} style={[styles.heading, television && styles.headingTelevision]}>
                {selected?.DeviceName ?? 'Choose a player'}
              </Text>
            </View>
            <FocusablePressable accessibilityLabel="Refresh players" onPress={() => { void remote.refresh(); }} style={(focused, pressed, hovered) => [styles.headerButton, (focused || hovered) && styles.headerButtonActive, pressed && styles.pressed]}>
              {remote.loading ? <ActivityIndicator color="#fff" size="small" /> : <RefreshCw color={colors.textMuted} size={17} />}
            </FocusablePressable>
            <FocusablePressable accessibilityLabel="Close remote playback" onPress={onClose} style={(focused, pressed, hovered) => [styles.headerButton, (focused || hovered) && styles.headerButtonActive, pressed && styles.pressed]}>
              <X color={colors.textMuted} size={18} />
            </FocusablePressable>
          </View>

          {selected && !showTargets ? (
            <>
              <View style={styles.nowPlaying}>
                <MediaImage
                  source={credentials && nowPlaying?.Id ? imageSource(credentials, nowPlaying, nowPlaying.BackdropImageTags?.length ? 'Backdrop' : 'Primary', 360) : undefined}
                  fallbackLabel={nowPlaying?.Name}
                  style={styles.artwork}
                />
                <View style={styles.nowPlayingCopy}>
                  <Text style={styles.nowPlayingLabel}>{nowPlaying ? (selected.PlayState?.IsPaused ? 'PAUSED' : 'NOW PLAYING') : 'READY'}</Text>
                  <Text numberOfLines={2} style={[styles.title, television && styles.titleTelevision]}>{nowPlaying?.Name ?? 'Select something to play'}</Text>
                  <Text numberOfLines={1} style={styles.subtitle}>{nowPlaying?.SeriesName ?? [selected.Client, selected.UserName].filter(Boolean).join(' · ')}</Text>
                </View>
              </View>

              {nowPlaying ? (
                <>
                  <FocusablePressable
                    accessibilityLabel="Remote playback timeline"
                    accessibilityRole="adjustable"
                    accessibilityValue={{ min: 0, max: Math.round(progress.durationTicks / 10_000_000), now: Math.round(progress.positionTicks / 10_000_000) }}
                    onLayout={(event) => setTimelineWidth(Math.max(1, event.nativeEvent.layout.width))}
                    onPress={(event) => issue(remote.seek((event.nativeEvent.locationX / timelineWidth) * progress.durationTicks))}
                    style={(focused) => [styles.timelineTouch, focused && styles.timelineFocused]}
                  >
                    <View style={styles.timelineTrack}>
                      <View style={[styles.timelinePlayed, { flex: Math.max(0.001, progress.ratio) }]} />
                      <View style={{ flex: Math.max(0.001, 1 - progress.ratio) }} />
                    </View>
                  </FocusablePressable>
                  <View style={styles.timeRow}>
                    <Text style={styles.timeText}>{formatRemoteTime(progress.positionTicks)}</Text>
                    <Text style={styles.timeText}>{formatRemoteTime(progress.durationTicks)}</Text>
                  </View>

                  <View style={styles.transportRow}>
                    <RemoteButton label="Previous item" disabled={!supports('PreviousTrack') || remote.commandPending} onPress={() => issue(remote.playstate('PreviousTrack'))}><ChevronLeft color="#fff" size={20} /></RemoteButton>
                    <RemoteButton label="Seek backward 10 seconds" disabled={!selected.PlayState?.CanSeek || remote.commandPending} onPress={() => seekBy(-10)}><RotateCcw color="#fff" size={19} /></RemoteButton>
                    <RemoteButton label={selected.PlayState?.IsPaused ? 'Resume remote playback' : 'Pause remote playback'} emphasized disabled={remote.commandPending} onPress={() => issue(remote.playstate(selected.PlayState?.IsPaused ? 'Unpause' : 'Pause'))}>
                      {selected.PlayState?.IsPaused ? <Play color="#0b0d10" fill="#0b0d10" size={22} /> : <Pause color="#0b0d10" fill="#0b0d10" size={22} />}
                    </RemoteButton>
                    <RemoteButton label="Seek forward 10 seconds" disabled={!selected.PlayState?.CanSeek || remote.commandPending} onPress={() => seekBy(10)}><RotateCw color="#fff" size={19} /></RemoteButton>
                    <RemoteButton label="Next item" disabled={!supports('NextTrack') || remote.commandPending} onPress={() => issue(remote.playstate('NextTrack'))}><ChevronRight color="#fff" size={20} /></RemoteButton>
                  </View>

                  <View style={styles.volumeRow}>
                    <RemoteButton label="Lower remote volume" disabled={remote.commandPending} onPress={() => issue(remote.setVolume(volume - 5))}><Minus color="#fff" size={17} /></RemoteButton>
                    <RemoteButton label={selected.PlayState?.IsMuted ? 'Unmute remote player' : 'Mute remote player'} disabled={remote.commandPending} onPress={() => issue(remote.toggleMute())}>
                      {selected.PlayState?.IsMuted ? <VolumeX color="#fff" size={18} /> : <Volume2 color="#fff" size={18} />}
                    </RemoteButton>
                    <View style={styles.volumeCopy}><Text style={styles.volumeLabel}>VOLUME</Text><Text style={styles.volumeValue}>{selected.PlayState?.IsMuted ? 'Muted' : `${volume}%`}</Text></View>
                    <RemoteButton label="Raise remote volume" disabled={remote.commandPending} onPress={() => issue(remote.setVolume(volume + 5))}><Plus color="#fff" size={17} /></RemoteButton>
                    <RemoteButton label="Stop remote playback" disabled={remote.commandPending} onPress={() => issue(remote.playstate('Stop'))}><Square color="#fff" fill="#fff" size={13} /></RemoteButton>
                  </View>
                </>
              ) : null}

              <View style={styles.footerRow}>
                <FocusablePressable accessibilityLabel="Switch remote player" onPress={() => setShowTargets(true)} style={(focused, pressed, hovered) => [styles.secondaryAction, (focused || hovered) && styles.secondaryActionActive, pressed && styles.pressed]}><Cast color={colors.textMuted} size={16} /><Text style={styles.secondaryActionText}>Switch player</Text></FocusablePressable>
                <FocusablePressable accessibilityLabel="Disconnect remote player" onPress={() => remote.selectSession(null)} style={(focused, pressed, hovered) => [styles.secondaryAction, (focused || hovered) && styles.secondaryActionActive, pressed && styles.pressed]}><Unplug color={colors.textMuted} size={16} /><Text style={styles.secondaryActionText}>Disconnect</Text></FocusablePressable>
              </View>
            </>
          ) : (
            <View style={styles.targets}>
              <Text style={styles.sectionLabel}>AVAILABLE PLAYERS</Text>
              <FocusablePressable accessibilityLabel="Play on this device" onPress={() => { remote.selectSession(null); setShowTargets(false); }} style={(focused, pressed, hovered) => [styles.target, (focused || hovered) && styles.targetActive, pressed && styles.pressed]}>
                <View style={styles.targetIcon}><Play color={colors.textMuted} size={16} /></View>
                <View style={styles.targetCopy}><Text style={styles.targetName}>This device</Text><Text style={styles.targetMeta}>Jellyflix</Text></View>
                {!selected ? <Check color={colors.brand} size={18} strokeWidth={2.6} /> : null}
              </FocusablePressable>
              {remote.sessions.map((session) => {
                const id = getRemoteSessionId(session);
                const active = id === remote.selectedSessionId;
                return (
                  <FocusablePressable key={id} accessibilityLabel={`Play on ${session.DeviceName ?? session.Client ?? 'remote player'}`} accessibilityState={{ selected: active }} onPress={() => { remote.selectSession(session); setShowTargets(false); }} style={(focused, pressed, hovered) => [styles.target, active && styles.targetSelected, (focused || hovered) && styles.targetActive, pressed && styles.pressed]}>
                    <View style={[styles.targetIcon, active && styles.targetIconSelected]}><Cast color={active ? '#fff' : colors.textMuted} size={16} /></View>
                    <View style={styles.targetCopy}><Text numberOfLines={1} style={styles.targetName}>{session.DeviceName ?? 'Remote player'}</Text><Text numberOfLines={1} style={styles.targetMeta}>{[session.Client, session.UserName].filter(Boolean).join(' · ') || 'Jellyfin client'}</Text></View>
                    {active ? <Check color={colors.brand} size={18} strokeWidth={2.6} /> : null}
                  </FocusablePressable>
                );
              })}
              {!remote.loading && remote.sessions.length === 0 ? <View style={styles.empty}><Text style={styles.emptyTitle}>No players found</Text><Text style={styles.emptyText}>Open MPV Shim or another remote-enabled Jellyfin client, then refresh.</Text></View> : null}
              {selected ? <FocusablePressable accessibilityLabel="Back to remote controls" onPress={() => setShowTargets(false)} style={(focused, pressed, hovered) => [styles.backAction, (focused || hovered) && styles.secondaryActionActive, pressed && styles.pressed]}><ChevronLeft color={colors.textMuted} size={16} /><Text style={styles.secondaryActionText}>Back to controls</Text></FocusablePressable> : null}
            </View>
          )}
          {remote.error ? <Text style={styles.error}>{remote.error}</Text> : null}
        </Animated.View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { position: 'relative', zIndex: 50 },
  trigger: { width: 34, height: 34, borderWidth: 1, borderColor: 'transparent', borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  triggerSelected: { borderColor: 'rgba(229,9,20,0.46)', backgroundColor: 'rgba(229,9,20,0.20)' },
  triggerActive: { borderColor: 'rgba(255,255,255,0.20)', backgroundColor: 'rgba(255,255,255,0.10)' },
  liveDot: { position: 'absolute', right: 4, bottom: 4, width: 5, height: 5, borderRadius: 3, backgroundColor: colors.brand, shadowColor: colors.brand, shadowOpacity: 0.8, shadowRadius: 4 },
  panel: { position: 'absolute', top: 42, right: 0, width: 360, maxHeight: 650, padding: 14, borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', borderRadius: 16, backgroundColor: 'rgba(11,14,19,0.97)', shadowColor: '#000', shadowOpacity: 0.68, shadowRadius: 24, shadowOffset: { width: 0, height: 10 }, elevation: 40 },
  sideUtilityHoverLabel: { position: 'absolute', left: 38, top: 1, minHeight: 30, justifyContent: 'center', paddingHorizontal: 10, borderRadius: 5, backgroundColor: 'rgba(12,14,18,0.92)', shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 8, elevation: 8 },
  sideUtilityLabelText: { color: colors.text, fontFamily: systemSansFont, fontSize: 13, lineHeight: 16, fontWeight: '600' },
  sidePanel: { top: -650, bottom: undefined, left: 42, right: undefined, maxHeight: 650 },
  panelTelevision: { width: 430, padding: 18, borderRadius: 18 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 13 },
  headerCopy: { flex: 1, minWidth: 0 },
  heading: { color: '#fff', fontFamily: systemSansFont, fontSize: 17, lineHeight: 21, fontWeight: '700' },
  headingTelevision: { fontSize: 21, lineHeight: 26 },
  headerButton: { width: 32, height: 32, borderRadius: 9, borderWidth: 1, borderColor: 'transparent', alignItems: 'center', justifyContent: 'center' },
  headerButtonActive: { borderColor: 'rgba(255,255,255,0.14)', backgroundColor: 'rgba(255,255,255,0.08)' },
  nowPlaying: { minHeight: 82, flexDirection: 'row', gap: 12, padding: 9, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.055)' },
  artwork: { width: 112, height: 64, borderRadius: 8 },
  nowPlayingCopy: { flex: 1, minWidth: 0, justifyContent: 'center' },
  nowPlayingLabel: { color: 'rgba(255,255,255,0.42)', fontFamily: systemSansFont, fontSize: 8, lineHeight: 11, fontWeight: '900', letterSpacing: 1 },
  title: { marginTop: 3, color: '#fff', fontFamily: systemSansFont, fontSize: 14, lineHeight: 18, fontWeight: '700' },
  titleTelevision: { fontSize: 17, lineHeight: 22 },
  subtitle: { marginTop: 3, color: 'rgba(255,255,255,0.50)', fontFamily: systemSansFont, fontSize: 10, lineHeight: 14, fontWeight: '500' },
  timelineTouch: { height: 22, marginTop: 12, justifyContent: 'center', borderRadius: 8 },
  timelineFocused: { backgroundColor: 'rgba(255,255,255,0.06)' },
  timelineTrack: { height: 4, flexDirection: 'row', overflow: 'hidden', borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.16)' },
  timelinePlayed: { backgroundColor: colors.brand },
  timeRow: { marginTop: -2, flexDirection: 'row', justifyContent: 'space-between' },
  timeText: { color: 'rgba(255,255,255,0.42)', fontFamily: systemSansFont, fontSize: 9, lineHeight: 12, fontWeight: '600', fontVariant: 'tabular-nums' as any },
  transportRow: { marginTop: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  transportButton: { width: 38, height: 38, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', borderRadius: 11, backgroundColor: 'rgba(255,255,255,0.07)', alignItems: 'center', justifyContent: 'center' },
  transportButtonPrimary: { width: 46, height: 46, borderColor: '#fff', borderRadius: 14, backgroundColor: '#fff' },
  transportButtonActive: { borderColor: 'rgba(255,255,255,0.42)', backgroundColor: 'rgba(255,255,255,0.15)', transform: [{ scale: 1.045 }] },
  volumeRow: { marginTop: 12, minHeight: 42, flexDirection: 'row', alignItems: 'center', gap: 7, paddingTop: 10, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: 'rgba(255,255,255,0.10)' },
  volumeCopy: { flex: 1, minWidth: 0, paddingHorizontal: 5 },
  volumeLabel: { color: 'rgba(255,255,255,0.34)', fontFamily: systemSansFont, fontSize: 8, lineHeight: 10, fontWeight: '900', letterSpacing: 1 },
  volumeValue: { marginTop: 1, color: 'rgba(255,255,255,0.74)', fontFamily: systemSansFont, fontSize: 11, lineHeight: 14, fontWeight: '600' },
  footerRow: { marginTop: 12, flexDirection: 'row', gap: 8 },
  secondaryAction: { flex: 1, minHeight: 38, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)', borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.035)' },
  secondaryActionActive: { borderColor: 'rgba(255,255,255,0.24)', backgroundColor: 'rgba(255,255,255,0.09)' },
  secondaryActionText: { color: 'rgba(255,255,255,0.70)', fontFamily: systemSansFont, fontSize: 11, lineHeight: 15, fontWeight: '600' },
  targets: { gap: 5 },
  sectionLabel: { paddingHorizontal: 5, paddingBottom: 4, color: 'rgba(255,255,255,0.34)', fontFamily: systemSansFont, fontSize: 9, lineHeight: 12, fontWeight: '900', letterSpacing: 1.1 },
  target: { minHeight: 55, flexDirection: 'row', alignItems: 'center', gap: 11, paddingHorizontal: 9, paddingVertical: 7, borderWidth: 1, borderColor: 'transparent', borderRadius: 11 },
  targetSelected: { borderColor: 'rgba(229,9,20,0.30)', backgroundColor: 'rgba(229,9,20,0.10)' },
  targetActive: { borderColor: 'rgba(255,255,255,0.13)', backgroundColor: 'rgba(255,255,255,0.07)' },
  targetIcon: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.07)' },
  targetIconSelected: { backgroundColor: 'rgba(229,9,20,0.65)' },
  targetCopy: { flex: 1, minWidth: 0 },
  targetName: { color: '#fff', fontFamily: systemSansFont, fontSize: 13, lineHeight: 17, fontWeight: '600' },
  targetMeta: { marginTop: 2, color: 'rgba(255,255,255,0.44)', fontFamily: systemSansFont, fontSize: 10, lineHeight: 13, fontWeight: '500' },
  empty: { marginTop: 4, padding: 14, borderRadius: 11, backgroundColor: 'rgba(255,255,255,0.04)' },
  emptyTitle: { color: 'rgba(255,255,255,0.80)', fontFamily: systemSansFont, fontSize: 13, lineHeight: 17, fontWeight: '700' },
  emptyText: { marginTop: 4, color: 'rgba(255,255,255,0.46)', fontFamily: systemSansFont, fontSize: 11, lineHeight: 16 },
  backAction: { minHeight: 38, marginTop: 5, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 9, borderWidth: 1, borderColor: 'transparent' },
  error: { marginTop: 10, padding: 9, borderRadius: 8, color: '#ffc8cb', backgroundColor: 'rgba(229,9,20,0.16)', fontFamily: systemSansFont, fontSize: 10, lineHeight: 14, fontWeight: '600' },
  pressed: { opacity: 0.72, transform: [{ scale: 0.97 }] },
  disabled: { opacity: 0.34 },
});
